// src/pages/ConsultingDetail.js
import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { FaPlus, FaMinus } from 'react-icons/fa';
import './ConsultingDetail.css';

const ConsultingDetail = ({ consulting, consultingData }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Detect mobile via JS (no CSS dependency)
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 1023px)').matches : false
  );
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 1023px)');
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener ? mql.addEventListener('change', handler) : mql.addListener(handler);
    return () => {
      mql.removeEventListener ? mql.removeEventListener('change', handler) : mql.removeListener(handler);
    };
  }, []);

  const items = useMemo(() => consultingData || [], [consultingData]);

  const currentPath = location.pathname;
  const activeItem = consulting || items.find((i) => i.path === currentPath) || null;
  const isAllConsultings = !activeItem;

  // Track which accordion item is open
  const [expandedItems, setExpandedItems] = useState([]);

  // Auto-expand the active item when landing on a specific route (no scrolling)
  useEffect(() => {
    if (activeItem?.path) setExpandedItems([activeItem.path]);
    else setExpandedItems([]);
  }, [activeItem?.path]);

  const toggleItem = (path) => {
    setExpandedItems((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [path]
    );
  };

  // Clicking an accordion header should update the URL and open that item
  const handleSelect = (item) => {
    if (currentPath !== item.path) navigate(item.path);
    setExpandedItems([item.path]);
  };

  return (
    <>
      <div className="consulting-detail-container">
        {/* DESKTOP detail view (sidebar + main) */}
        {!isAllConsultings && !isMobile && (
          <div className="consulting-grid">
            <aside className="consulting-sidebar" aria-label="Consulting Navigation">
              <ul>
                {items.map((item, index) => (
                  <li key={index}>
                    <NavLink
                      to={item.path}
                      className={({ isActive }) =>
                        isActive ? 'consulting-link active' : 'consulting-link'
                      }
                    >
                      {item.title}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </aside>

            <main className="consulting-main-content">
              <h1>{activeItem.title}</h1>
              <p>{activeItem.description}</p>
            </main>
          </div>
        )}

        {/* Title for the all-consulting view */}
        {isAllConsultings && <h2 className="consulting-title">Our Consulting Services</h2>}

        {/* ACCORDION: shown on mobile always; also shown on desktop for the "all" view (mirrors your original layout) */}
        {(isMobile || isAllConsultings) && (
          <div className="consulting-accordion" role="list">
            {items.map((item, index) => {
              const isExpanded = expandedItems.includes(item.path);
              const isActive = activeItem && activeItem.path === item.path;

              return (
                <div className={`accordion-item ${isActive ? 'is-active' : ''}`} key={index} role="listitem">
                  <div
                    id={`accordion-header-${index}`}
                    className={`accordion-header ${isExpanded ? 'active' : ''}`}
                    onClick={() => handleSelect(item)}
                    role="button"
                    aria-expanded={isExpanded}
                    aria-controls={`accordion-content-${index}`}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handleSelect(item);
                    }}
                  >
                    <span>{item.title}</span>
                    <span className="accordion-icon">
                      {isExpanded ? <FaMinus /> : <FaPlus />}
                    </span>
                  </div>
                  <div
                    id={`accordion-content-${index}`}
                    className="accordion-content"
                    role="region"
                    aria-labelledby={`accordion-header-${index}`}
                  >
                    {isExpanded && <p>{item.description}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default ConsultingDetail;
